// src/components/layout/NotificationBell.tsx
// Notification bell icon with badge + dropdown panel for the top navbar.

import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassTop as PendingIcon,
  Description as DocIcon,
  Psychology as LLMIcon,
  CloudUpload as UploadIcon,
  Download as ExportIcon,
  FileDownload as DownloadReportIcon,
  Replay as RetryIcon,
  DoneAll as DoneAllIcon,
  OpenInNew as OpenIcon,
  GroupAdd as GroupAddIcon,
  Check as AcceptCheckIcon,
  Close as DeclineIcon,
  DeleteSweep as ClearAllIcon,
  CreditCardOff as PaymentFailedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import useNotifications, { TaskNotification } from "hooks/useNotifications";
import { useTranslation } from "../../i18n";
import { anonymizeName } from "components/PatientDetails/panels/DataPanel";
import api from "../../api/api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusIcon(status: TaskNotification["status"]) {
  switch (status) {
    case "completed":
      return <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />;
    case "failed":
      return <ErrorIcon sx={{ fontSize: 18, color: "error.main" }} />;
    case "in_progress":
      return <CircularProgress size={18} color="info" />;
    case "pending":
      return <PendingIcon sx={{ fontSize: 18, color: "warning.main" }} />;
    default:
      return null;
  }
}

function taskTypeIcon(taskType: TaskNotification["task_type"]) {
  switch (taskType) {
    case "document_upload":
      return <DocIcon sx={{ fontSize: 16, opacity: 0.7 }} />;
    case "llm_extraction":
      return <LLMIcon sx={{ fontSize: 16, opacity: 0.7 }} />;
    case "bulk_upload":
    case "bulk_extraction":
      return <UploadIcon sx={{ fontSize: 16, opacity: 0.7 }} />;
    case "export":
      return <ExportIcon sx={{ fontSize: 16, opacity: 0.7 }} />;
    case "environment_invite":
      return <GroupAddIcon sx={{ fontSize: 16, opacity: 0.7 }} />;
    case "payment_failed":
      return <PaymentFailedIcon sx={{ fontSize: 16, color: "error.main" }} />;
    default:
      return null;
  }
}

function timeAgo(isoDate: string, t: (key: string, defaultValue: string) => string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("notification_bell.just_now", "just now");
  if (mins < 60) return t("notification_bell.mins_ago", "{mins}m ago").replace("{mins}", String(mins));
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t("notification_bell.hours_ago", "{hours}h ago").replace("{hours}", String(hours));
  const days = Math.floor(hours / 24);
  return t("notification_bell.days_ago", "{days}d ago").replace("{days}", String(days));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface NotificationBellProps {
  onActiveTasksChange?: (active: boolean, progress: number) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ onActiveTasksChange }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Auto-toast callback: fires a snackbar when a notification completes or fails.
  // Suppress intermediate "document_upload" completions — the user should only
  // see a toast after the full pipeline finishes (llm_extraction or bulk_extraction).
  const handleNewNotification = React.useCallback(
    (n: TaskNotification) => {
      if (n.task_type === "document_upload" && n.status === "completed") return;

      if (n.status === "completed") {
        enqueueSnackbar(n.title, { variant: "success", autoHideDuration: 4000 });
      } else if (n.status === "failed") {
        enqueueSnackbar(n.title, { variant: "error", autoHideDuration: 6000 });
      }
    },
    [enqueueSnackbar],
  );

  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAll,
    hasActiveTasks,
  } = useNotifications(handleNewNotification);

  React.useEffect(() => {
    let progress = -1;
    if (hasActiveTasks) {
      const active = notifications.filter(
        (n) => n.status === "pending" || n.status === "in_progress"
      );
      let totalItems = 0;
      let doneItems = 0;
      let hasBulkMeta = false;
      for (const n of active) {
        const meta = n.metadata as Record<string, number | string | undefined>;
        const total = meta?.patients_total;
        const done = meta?.patients_done;
        if (typeof total === "number" && total > 0) {
          hasBulkMeta = true;
          totalItems += total;
          doneItems += typeof done === "number" ? done : 0;
        } else {
          totalItems += 1;
          const status = String(meta?.processing_status || n.status || "");
          if (status === "pending") doneItems += 0.1;
          else if (status === "queued") doneItems += 0.2;
          else if (status === "in_progress" || status === "processing") doneItems += 0.5;
          else if (status === "completed" || status === "success") doneItems += 1;
          else doneItems += 0.3;
        }
      }
      if (totalItems > 0) {
        progress = Math.min(99, Math.max(5, Math.round((doneItems / totalItems) * 100)));
      }
    }
    onActiveTasksChange?.(hasActiveTasks, progress);
  }, [hasActiveTasks, notifications, onActiveTasksChange]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications(); // refresh immediately on open
  };
  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = (n: TaskNotification) => {
    if (n.task_type === "environment_invite") return;

    if (!n.read_at) markAsRead(n.id);

    if (n.task_type === "payment_failed") {
      navigate("/billing");
      handleClose();
      return;
    }

    if (n.task_type === "bulk_extraction" || n.task_type === "bulk_upload") {
      const jobId = (n.metadata as Record<string, unknown>)?.job_id;
      if (jobId) {
        navigate(`/bulk-upload/results/${jobId}`);
        handleClose();
        return;
      }
    }

    if (n.patient_id) {
      navigate(`/patients/${n.patient_id}/details`);
      handleClose();
    }
  };

  const handleAcceptInvite = async (n: TaskNotification) => {
    const membershipId = n.metadata?.membership_id;
    if (!membershipId) return;
    setActionLoading(n.id);
    try {
      await api.post(`/environments/invitations/${membershipId}/accept/`);
      enqueueSnackbar(t("notification_bell.invitation_accepted", "Invitation accepted! You now have access to the environment."), { variant: "success" });
      fetchNotifications();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t("notifications.accept_failed", "Failed to accept invitation"), { variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineInvite = async (n: TaskNotification) => {
    const membershipId = n.metadata?.membership_id;
    if (!membershipId) return;
    setActionLoading(n.id);
    try {
      await api.post(`/environments/invitations/${membershipId}/decline/`);
      enqueueSnackbar(t("notification_bell.invitation_declined", "Invitation declined."), { variant: "info" });
      fetchNotifications();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t("notification_bell.failed_decline", "Failed to decline invitation"), { variant: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const isInvitePending = (n: TaskNotification) =>
    n.task_type === "environment_invite" && n.status === "pending" && !n.read_at;

  // --- Bulk extraction failure actions ---
  const isBulkWithFailures = (n: TaskNotification) =>
    n.task_type === "bulk_extraction" &&
    !!n.metadata?.has_failure_report &&
    (n.status === "completed" || n.status === "failed");

  const handleBulkRetry = async (n: TaskNotification) => {
    const jobId = n.metadata?.job_id as string | undefined;
    if (!jobId) return;
    setActionLoading(n.id);
    try {
      await api.post(`/patients/bulk-upload/resume/${jobId}/`);
      enqueueSnackbar(
        t("notification_bell.bulk_retry_started", "Retrying failed patients..."),
        { variant: "info" },
      );
      fetchNotifications();
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error ||
          t("notification_bell.bulk_retry_failed", "Failed to retry"),
        { variant: "error" },
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkDownloadReport = async (n: TaskNotification) => {
    const jobId = n.metadata?.job_id as string | undefined;
    if (!jobId) return;
    setActionLoading(n.id);
    try {
      const res = await api.get(`/patients/bulk-upload/status/${jobId}/`);
      const report = res.data?.failure_report;
      if (report) {
        const blob = new Blob([JSON.stringify(report, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bulk_upload_report_${jobId.slice(0, 8)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        enqueueSnackbar(
          t("notification_bell.no_report", "No failure report available"),
          { variant: "warning" },
        );
      }
    } catch (err: any) {
      enqueueSnackbar(
        t("notification_bell.download_failed", "Failed to download report"),
        { variant: "error" },
      );
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <IconButton
        data-tour="notifications"
        onClick={handleOpen}
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: alpha(theme.palette.secondary.main, 0.15),
          color: theme.palette.secondary.main,
          "&:hover": {
            background: alpha(theme.palette.secondary.main, 0.25),
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "0.65rem",
              height: 18,
              minWidth: 18,
            },
          }}
        >
          {unreadCount > 0 ? (
            <NotificationsIcon fontSize="small" />
          ) : (
            <NotificationsNoneIcon fontSize="small" />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 400,
              maxHeight: 520,
              mt: 1,
              borderRadius: 2,
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {t("notifications.title", "Notifications")}
            {hasActiveTasks && (
              <CircularProgress size={14} sx={{ ml: 1 }} />
            )}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                onClick={markAllAsRead}
                sx={{ fontSize: "0.75rem", textTransform: "none" }}
              >
                {t("notifications.mark_all_read", "Mark all read")}
              </Button>
            )}
            {notifications.some((n) => n.read_at) && (
              <Button
                size="small"
                startIcon={<ClearAllIcon sx={{ fontSize: 16 }} />}
                onClick={clearAll}
                color="inherit"
                sx={{ fontSize: "0.75rem", textTransform: "none", opacity: 0.7 }}
              >
                {t("notifications.clear_all", "Clear")}
              </Button>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Notification list */}
        <Box sx={{ maxHeight: 420, overflow: "auto" }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <NotificationsNoneIcon
                sx={{ fontSize: 40, opacity: 0.3, mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {t("notifications.empty", "No notifications")}
              </Typography>
            </Box>
          ) : (
            <Stack>
              {notifications.map((n) => (
                <Box
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    cursor: (n.patient_id && n.task_type !== "environment_invite") || n.task_type === "payment_failed" || n.task_type === "bulk_extraction" || n.task_type === "bulk_upload" ? "pointer" : "default",
                    transition: "background 0.15s",
                    bgcolor: n.read_at
                      ? "transparent"
                      : alpha(theme.palette.primary.main, 0.06),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {/* Status icon */}
                  <Box sx={{ pt: 0.3 }}>{statusIcon(n.status)}</Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 0.3,
                        }}
                      >
                        {taskTypeIcon(n.task_type)}
                        <Typography
                          variant="body2"
                          fontWeight={n.read_at ? 400 : 600}
                          noWrap
                          color="text.primary"
                        >
                          {n.title}
                        </Typography>
                      </Box>
                      {n.detail && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                        >
                          {n.detail}
                        </Typography>
                      )}
                      {/* Show failure count + action buttons for bulk extractions with errors */}
                      {n.task_type === "bulk_extraction" && !!n.metadata?.has_failure_report && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          display="block"
                          sx={{ mt: 0.25 }}
                        >
                          {`${String(n.metadata.failed_count ?? "")} ${t("notification_bell.patients_failed", "patient(s) failed")}`}
                        </Typography>
                      )}
                      {(n.task_type === "bulk_extraction" || n.task_type === "bulk_upload") &&
                        !!(n.metadata as Record<string, unknown>)?.job_id && (
                        <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<OpenIcon sx={{ fontSize: 14 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              const jobId = (n.metadata as Record<string, unknown>)?.job_id;
                              if (!n.read_at) markAsRead(n.id);
                              navigate(`/bulk-upload/results/${jobId}`);
                              handleClose();
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.7rem",
                              py: 0.25,
                              px: 1.2,
                              minHeight: 26,
                            }}
                          >
                            {t("notification_bell.review_results", "Review Results")}
                          </Button>
                          {isBulkWithFailures(n) && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={
                                  actionLoading === n.id ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <RetryIcon sx={{ fontSize: 16 }} />
                                  )
                                }
                                disabled={actionLoading === n.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBulkRetry(n);
                                }}
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.7rem",
                                  py: 0.25,
                                  px: 1.2,
                                  minHeight: 26,
                                }}
                              >
                                {t("notification_bell.retry_failed", "Retry")}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={
                                  actionLoading === n.id ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <DownloadReportIcon sx={{ fontSize: 16 }} />
                                  )
                                }
                                disabled={actionLoading === n.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBulkDownloadReport(n);
                                }}
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.7rem",
                                  py: 0.25,
                                  px: 1.2,
                                  minHeight: 26,
                                }}
                              >
                                {t("notification_bell.download_errors", "Download Errors")}
                              </Button>
                            </>
                          )}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 0.5,
                        }}
                      >
                        <>
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            fontSize="0.7rem"
                          >
                            {timeAgo(n.created_at, t)}
                          </Typography>
                          {(n.patient_name || n.patient_id) && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontSize="0.7rem"
                              sx={{ fontFamily: "monospace", letterSpacing: 0.5 }}
                            >
                              {`${n.patient_name ? anonymizeName(n.patient_name) : ""}${n.patient_id ? t("notification_bell.patient_id", " (ID: {id})").replace("{id}", String(n.patient_id)) : ""}`}
                            </Typography>
                          )}
                        </>
                      </Box>

                      {/* Accept / Decline buttons for environment invitations */}
                      {isInvitePending(n) && (
                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={actionLoading === n.id ? <CircularProgress size={14} /> : <AcceptCheckIcon sx={{ fontSize: 16 }} />}
                            disabled={actionLoading === n.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptInvite(n);
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              py: 0.3,
                              px: 1.5,
                              minHeight: 28,
                            }}
                          >
                            {t("notification_bell.accept", "Accept")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={actionLoading === n.id ? <CircularProgress size={14} /> : <DeclineIcon sx={{ fontSize: 16 }} />}
                            disabled={actionLoading === n.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeclineInvite(n);
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              py: 0.3,
                              px: 1.5,
                              minHeight: 28,
                            }}
                          >
                            {t("notification_bell.decline", "Decline")}
                          </Button>
                        </Box>
                      )}

                      {/* Show result for handled invitations */}
                      {n.task_type === "environment_invite" && n.status === "completed" && (
                        <Typography variant="caption" color="success.main" fontWeight={600} sx={{ mt: 0.5 }} display="block">
                          Accepted
                        </Typography>
                      )}
                      {n.task_type === "environment_invite" && n.status === "failed" && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }} display="block">
                          {t("notification_bell.declined", "Declined")}
                        </Typography>
                      )}
                    </>
                  </Box>

                  {/* Navigate arrow (not for environment invites) */}
                  {((n.patient_id && n.task_type !== "environment_invite") || n.task_type === "payment_failed" || n.task_type === "bulk_extraction" || n.task_type === "bulk_upload") && (
                    <Box sx={{ pt: 0.5 }}>
                      <OpenIcon
                        sx={{ fontSize: 16, opacity: 0.4 }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
